import React from "react";
import {
  DataGrid,
  GridRowsProp,
  GridColDef,
  useGridSelector,
  useGridApiContext,
  gridPageCountSelector,
  GridPagination,
  GridRowParams,
  GridValidRowModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridSortModel,
} from "@mui/x-data-grid";

import {
  LinearProgress,
  Stack,
  TablePaginationProps,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import { Container } from "../styledComponents";
import NoResults from "./NoResults";
import { useNavigate } from "react-router-dom";
import { fontWeight } from "html2canvas/dist/types/css/property-descriptors/font-weight";

interface Props<T extends GridValidRowModel> {
  rows: GridRowsProp;
  columns: GridColDef[];
  loading?: boolean;
  clickable?: boolean;
  page: string;
  onClick?: (params: GridRowParams<T>) => void;
  slots?: {
    toolbar?: React.ComponentType<any>;
  };
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

export default function Table<T extends GridValidRowModel>(props: Props<T>) {
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });

  const { loading, onClick } = props;
  const navigate = useNavigate();

  function Pagination({
    page,
    onPageChange,
    className,
  }: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <MuiPagination
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          onPageChange(event as any, newPage - 1);
        }}
      />
    );
  }

  function CustomPagination(props: any) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
  }

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <GridToolbarQuickFilter sx={{ borderRadius: "100rem", flexGrow: 1, marginRight: "auto" }} />
        <GridToolbarExport sx={{ marginLeft: "auto" }} />
      </GridToolbarContainer>
    );
  }


  const renderHyperlink = (params: any) => {
    return (
      <a className="event-link">
        {params.value}
      </a>
    );
  };

  return (
    <TableWrapper style={{ height: 750, width: "100%",  }}>
      <DataGrid
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' } }}
        rows={props.rows}
        columns={props.columns.map((column, index) => ({
          ...column,
          renderCell:
            column.field === "event_name" || column.field === "ticket_name" && props.page === "my_tickets" ? renderHyperlink : column.renderCell,
          cellClassName:
            column.field === "event_name" || column.field === "ticket_name" && props.page === "my_tickets" ? "first-column-text" : "",
        }))}
        onRowClick={(props.clickable ?? true)?(params, _) => {
          if(props.page === "event_sales"){
            navigate("/ticket-sales", { state: { eventData: params.row } });
          } else if (props.page === "my_tickets"){
            window.open(`/payment-confirmation?receipt_id=${params.row.receipt_id}`, '_blank');
            // navigate("/payment-confirmation", { state: { eventData: params.row } });
          }
          onClick && onClick(params);
        }: ()=>{}}
        rowHeight={55}
        pagination
        slots={{
          pagination: CustomPagination,
          toolbar: props.slots?.toolbar || CustomToolbar,
          noResultsOverlay: () => (
            <NoResults
              header={"No Result Found!"}
              body={"Nothing matched what you were looking for"}
            />
          ),
          noRowsOverlay: () => (
            <NoResults
              header={"Nothing yet!"}
              body={"Nothing matched what you were looking for"}
            />
          ),
        }}
        slotProps={{
          toolbar: {
            printOptions: {
              fields: props.columns.map((column: any) => column.field),
            },
            csvOptions: { disableToolbarButton: false },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 200 },
          },
        }}
        loading={loading}
        autoPageSize
        columnHeaderHeight={42}
        density="comfortable"
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 15, 20]}
        className="data-grid"
        classes={{
          columnHeader: "column-header",
          withBorderColor: "with-border-color",
          toolbarContainer: "toolbar-container",
          columnHeaderTitleContainer: "column-header-title-container",
          "cell--withRenderer": "cell--withRenderer",
          row: "row",
          footerContainer: "footer-container",
        }}
        getRowClassName={(params) => (params.id === 1 ? `first-row ${(params.indexRelativeToCurrentPage % 2 === 0 ? "" : "odd-row")}` : ` ${(params.indexRelativeToCurrentPage % 2 === 0 ? "" : "odd-row")}`)}

      />
    </TableWrapper>
  );
}

const TableWrapper = styled(Container)(({ theme }) => ({
  ".data-grid": {
    border: "none !important",
    color: theme.typography.body1.color,
  },
  ".MuiDataGrid-row, .MuiDataGrid-cell": {
    borderBottom: "none !important",
  },
  ".column-header": {
    background: theme.palette.background.paper,
    fontSize: "16px",
    color:"rgba(95, 100, 98, 1)",
    fontFamily: "Gilroy-Bold",
    // borderTop: "3px dashed rgba(0, 0, 0, 1)", // Thicker dashes with increased width
    borderTop: "2.5px dashed rgba(0, 0, 0, 1)",
    borderBottom: "2.5px dashed rgba(0, 0, 0, 1)",
    fontWeight:"600"
    
  },
  ".with-border-color": {
    borderColor: "rgb(229, 231, 234)",
  },
  ".MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within": {
    outline: "none !important",
  },
  ".odd-row": {
    backgroundColor: "rgba(225, 230, 234, 1)", // Light red background for odd rows
  },
  ".column-header-title-container": {
    ".MuiCheckbox-root": {
      svg: {},
      "&.Mui-checked": {
        svg: {},
      },
    },
  },
  ".cell--withRenderer": {
    ".Mui-checked": {
      svg: {},
    },
    svg: {},
  },
  // ".row": {
  //   fontSize: "12px",
  //   cursor: "pointer",
  // },
  ".row": {
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    textAlign: "left",
    color: "rgba(95, 100, 98, 1)",
    cursor: "pointer",
    border: "none !important",
  },
  ".first-row": {
    cursor: "pointer",
  },
  ".first-column-text": {
    color: "blue",
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  },

  ".footer-container": {
    border: "none !important",
    marginTop: "32px",
    ".MuiDataGrid-selectedRowCount, .MuiTablePagination-displayedRows": {
      fontSize: "12px",
      padding: "8px 12px",
      borderRadius: "8px",
    },
    ".MuiPagination-root": {
      ".MuiPaginationItem-root.Mui-selected": {},
    },
  },

  ".toolbar-container": {
    marginBottom: "18px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    button: {
      padding: "10px 20px",
      borderRadius: "20px",
    },
    ".MuiTextField-root": {
      ".MuiInput-root": {
        // Remove underline and highlight
        borderRadius: "100rem !important",
        maxWidth: "250px",
        padding: "5px 12px",
        background: "rgba(237, 242, 247, 0.3)",
        border: "none",
        svg: {
          display: "none",
        },
      },
      ".MuiInput-root:before, .MuiInput-root:after": {
        border: "none !important", // Ensure no underline appears on focus
      },
    },
  },
}));

